import { reactive, computed, onUnmounted } from 'vue';
import { withCache, cacheDurationTypes } from '@/Utils/CacheUtility.js';
import { guid } from '@/Utils';

export function useApiFetch() {
    const cacheTime = cacheDurationTypes.short; // Default to 1 minute
    const state = reactive({
        isProcessing: false,
        error: null,
        data: null,
        fromCache: false,
    });

    const activeRequests = new Set();

    // Computed properties
    const isProcessing = computed(() => state.isProcessing);
    const error = computed(() => state.error);
    const data = computed(() => state.data);
    const hasError = computed(() => !!state.error);
    const fromCache = computed(() => state.fromCache);

    // Generic fetch function with caching via withCache
    const execute = async (apiCall, options = {}) => {
        if (!apiCall || typeof apiCall !== 'function') {
            throw new Error('apiCall must be a function');
        }

        const {
            onSuccess,
            onError,
            resetDataOnStart = true,
            signal,
            cache: cacheOptions = {},
        } = options;

        const {
            enabled = false,
            key = guid(),
            cacheDuration = cacheTime, // 1 minute
            skipCache = false,
            ...restCacheOptions
        } = cacheOptions;

        // Create AbortController if not provided
        const controller = new AbortController();
        const abortSignal = signal || controller.signal;
        const isCancelled = () => controller.signal.aborted || abortSignal.aborted;

        activeRequests.add(controller);

        const startTime = Date.now();

        const response = {
            success: false,
            data: null,
            fromCache: false,
            cancelled: false,
            error: null,
            metadata: {
                requestId: key,
                duration: 0,
                timestamp: new Date().toISOString(),
            },
        };

        try {
            state.isProcessing = true;
            state.error = null;
            state.fromCache = false;

            if (resetDataOnStart) {
                state.data = null;
            }

            let result;

            if (enabled) {
                // Use existing withCache utility
                const cachedApiCall = withCache(
                    async (...args) => {
                        const apiResult = await apiCall(abortSignal, ...args);
                        return apiResult;
                    },
                    {
                        key,
                        cacheDuration,
                        skipCache,
                        ...restCacheOptions,
                    },
                );

                result = await cachedApiCall();

                // Check if result came from cache (has expiresAt property)
                response.fromCache = result && result.expiresAt !== undefined;

                // If from cache, unwrap the actual data
                if (response.fromCache) {
                    result = result.value || result;
                }
            }
            else {
                // No caching, direct API call
                result = await apiCall(abortSignal);
            }

            // Check if request was cancelled
            if (isCancelled()) {
                response.cancelled = true;
                return response;
            }

            state.data = result;
            state.fromCache = response.fromCache;

            response.success = true;
            response.data = result;

            if (onSuccess) {
                await onSuccess(response);
            }

        }
        catch (err) {
            // Don't set error state if request was cancelled
            if (err.name === 'AbortError' || isCancelled()) {
                response.cancelled = true;
                return response;
            }

            state.error = err;
            response.error = err;

            if (onError) {
                onError(response);
            }

        }
        finally {
            activeRequests.delete(controller);
            state.isProcessing = activeRequests.size > 0;
            response.metadata.duration = Date.now() - startTime;
        }

        return response;
    };

    // Cancel all active requests
    const cancelAll = () => {
        activeRequests.forEach(controller => controller.abort());
        activeRequests.clear();
        state.isProcessing = false;
    };

    // Auto-cancel on component unmount
    onUnmounted(() => {
        cancelAll();
    });

    return {
        // State
        isProcessing,
        error,
        data,
        hasError,
        fromCache,

        // Methods
        execute,
        cancelAll,
        reset: () => {
            cancelAll();
            state.error = null;
            state.data = null;
            state.fromCache = false;
        },
    };
}